import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Use ref callbacks to measure React component size",
  "description": "",
  "published": "2018-06-07T00:00:00.000Z",
  "redirect_from": ["/blog/ref-callbacks-measure-react-component-size/swizec/8444"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`You can use ref callbacks to measure the rendered size of React components, did you know? It's a neat little trick.`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/71vx2q5r76",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`Here's how it works 👇 1. React renders your component 2. Browser layout engine does its thing 3. ref callback fires 4. Use `}<inlineCode parentName="p">{`getBoundingClientRect`}</inlineCode>{` to measure element size 5. Use this info for whatever you want`}</p>
    <p>{`We used this trick in this `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/build-animated-accordion-react-d3/swizec/8418"
      }}>{`Dynamic SVG accordion example`}</a>{` and in this `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/tiny-react-d3-flamegraph-tutorial/swizec/8440"
      }}>{`Tiny React & D3 flamegraph tutorial`}</a>{`. That's because layouting in SVG is hard, and you have to do everything yourself.`}</p>
    <p>{`Using ref callbacks to measure your elements is a little less useful in the modern HTML + CSS world. You can use flexbox and css-grid so you never need to know what you're dealing with.`}</p>
    <p>{`And yet when push comes to shove, sometimes you just really need your code to know the size of an element.`}</p>
    <p>{`A minimal size reporting component looks like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class ReportSize extends React.Component {
  refCallback = (element) => {
    if (element) {
      this.props.getSize(element.getBoundingClientRect());
    }
  };

  render() {
    return (
      <div ref={this.refCallback} style={{ border: "1px solid red" }}>
        {faker.lorem.paragraphs(Math.random() * 10)}
      </div>
    );
  }
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`render`}</inlineCode>{` method outputs a `}<inlineCode parentName="p">{`<div>`}</inlineCode>{` with a ref callback and a red border. Inside, we use `}<inlineCode parentName="p">{`faker`}</inlineCode>{` to generate up to 10 random paragraphs.`}</p>
    <p>{`After React places this element, it calls `}<inlineCode parentName="p">{`refCallback`}</inlineCode>{` with a reference to the rendered DOM node. We can then use `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect"
      }}><inlineCode parentName="a">{`getBoundingClientRect`}</inlineCode></a>{` to measure its size.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
  "x": 8,
  "y": 158.8125,
  "width": 544,
  "height": 340,
  "top": 158.8125,
  "right": 552,
  "bottom": 498.8125,
  "left": 8
}
`}</code></pre>
    <p>{`All sorts of useful info!`}</p>
    <h2 {...{
      "id": "so-why-not-just-use-componentdidmount"
    }}>{`So why not just use `}<inlineCode parentName="h2">{`componentDidMount`}</inlineCode>{`?`}</h2>
    <p>{`Yes, that works too. But it's less elegant because you have to save the `}<inlineCode parentName="p">{`ref`}</inlineCode>{` first. The `}<inlineCode parentName="p">{`refCallback`}</inlineCode>{` API calls your function with a nice reference already packaged in.`}</p>
    <p>{`However, you might still have to do that if your component size changes `}<em parentName="p">{`after`}</em>{` initial render. Observe 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/v6m4o48zl",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`Clicking the `}<inlineCode parentName="p">{`shuffle`}</inlineCode>{` button doesn't report new sizing information up the hierarchy. That's not good 🤔`}</p>
    <p>{`If your component changes size without re-mounting, you have to re-measure its size in `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{` as well. But that way lies trouble… you can fall into the infinite recursion trap.`}</p>
    <p>{`You can solve the problem with a lock, like this 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/7jqmo1jn8j",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`Keep clicking `}<inlineCode parentName="p">{`shuffle`}</inlineCode>{` and sizing info is always correct.`}</p>
    <p>{`The key is enabling size reporting when you logically know size is going to change, in `}<inlineCode parentName="p">{`shuffle`}</inlineCode>{`, and disabling it as soon as you report the change in `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`shuffle = () => {
    this.doReportSize = true;
    this.setState({
      text: faker.lorem.paragraphs(Math.random() * 10)
    });
  };

  refCallback = element => {
    if (element) {
      this.elementRef = element;
      this.props.getSize(element.getBoundingClientRect());
    }
  };

  componentDidUpdate() {
    if (this.doReportSize) {
      this.props.getSize(this.elementRef.getBoundingClientRect());
      this.doReportSize = false;
    }
  }
`}</code></pre>
    <p>{`Oof, not pretty. Setting a `}<inlineCode parentName="p">{`this.elementRef`}</inlineCode>{` in our callback, messing around with class properties for flags. Pretty sure we could've just used the new `}<inlineCode parentName="p">{`React.createRef()`}</inlineCode>{` API in combination with `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{` and `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`.`}</p>
    <p>{`Would still need the flag to prevent infinite loops, however.`}</p>
    <p>{`I wonder if using class properties to make render flags like that will continue to work when asynchronous rendering comes with React 17 🤔`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      